import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Chip,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material'
import {
  Save,
  Cancel,
  Description,
  Business,
  People,
  MenuBook,
  Gavel,
  Add,
  Edit,
  Delete,
  AccountTree
} from '@mui/icons-material'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'
import DiagramEditor from './DiagramEditor'

const ProcedimientoEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Datos del formulario
  const [formData, setFormData] = useState({
    codigo: '',
    titulo: '',
    objetivo: '',
    alcance: '',
    proceso: null,
    version: '1.0',
    gestion_id: '',
    responsables: [],
    definiciones: [],
    normatividades: [],
    normatividad_custom: '',
    estado: 'borrador'
  })

  // Actividades
  const [actividades, setActividades] = useState([])
  const [openActivityDialog, setOpenActivityDialog] = useState(false)
  const [editingActivity, setEditingActivity] = useState(null)
  const [activityForm, setActivityForm] = useState({
    titulo: '',
    descripcion: '',
    informacion_soporte: '',
    informacion_generada: '',
    responsables: []
  })

  // Datos adicionales
  const [gestiones, setGestiones] = useState([])
  const [roles, setRoles] = useState([])
  const [definiciones, setDefiniciones] = useState([])
  const [normatividades, setNormatividades] = useState([])
  const [procesos, setProcesos] = useState([])

  // Referencia para hacer scroll al diagrama
  const diagramRef = useRef(null)
  
  // Diagrama
  const [diagramCode, setDiagramCode] = useState('')
  const [diagramSvg, setDiagramSvg] = useState('')
  const [diagramExists, setDiagramExists] = useState(false)
  const [diagramHasUnsavedChanges, setDiagramHasUnsavedChanges] = useState(false)
  const [showUnsavedDiagramDialog, setShowUnsavedDiagramDialog] = useState(false)
  const [showCustomNormatividad, setShowCustomNormatividad] = useState(false)
  const [selectedGestion, setSelectedGestion] = useState(null)
  const [codigoInput, setCodigoInput] = useState('')

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'aprobado': return 'success'
      case 'revision': return 'warning'
      case 'borrador': return 'info'
      default: return 'default'
    }
  }

  useEffect(() => {
    loadProcedimiento()
    loadReferenceData()

    // Configurar auto-recarga cada 30 segundos para las opciones de los selects
    const interval = setInterval(() => {
      console.log('🔄 Auto-recargando opciones de selects en edición...')
      loadReferenceData()
    }, 30000) // 30 segundos

    // Cleanup interval al desmontar el componente
    return () => clearInterval(interval)
  }, [id])

  useEffect(() => {
    if (formData.gestion_id && gestiones.length > 0) {
      const gestion = gestiones.find(g => g.id == formData.gestion_id)
      setSelectedGestion(gestion)
      // Extraer la parte numérica del código actual
      if (formData.codigo) {
        const partes = formData.codigo.split('-')
        if (partes.length > 1) {
          setCodigoInput(partes.slice(1).join('-'))
        }
      }
    } else {
      setSelectedGestion(null)
      setCodigoInput('')
    }
  }, [formData.gestion_id, gestiones, formData.codigo])

  // Efecto para enfocar en el diagrama si se especifica en la URL
  useEffect(() => {
    const focus = searchParams.get('focus')
    if (focus === 'diagrama' && diagramRef.current && !loading) {
      // Hacer scroll suave hacia el diagrama después de que cargue la página
      setTimeout(() => {
        diagramRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
        // Mostrar mensaje de ayuda
        setSuccess('¡Ahora puedes crear el diagrama de flujo para tu procedimiento!')
        setTimeout(() => setSuccess(null), 4000)
      }, 500)
    }
  }, [searchParams, loading])

  const loadProcedimiento = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`/api/procedimientos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      const proc = response.data
      
      // Procesar normatividad para separar predefinidas de personalizadas
      const normatividadTexto = proc.normatividad || ''
      
      setFormData({
        codigo: proc.codigo || '',
        titulo: proc.titulo || '',
        objetivo: proc.objetivo || '',
        alcance: proc.alcance || '',
        proceso: proc.proceso || '',
        version: proc.version || '1.0',
        gestion_id: proc.gestion_id || '',
        responsables: proc.responsables || [],
        definiciones: [], // Se procesarán después de cargar las definiciones disponibles
        normatividades: [], // Se procesarán después de cargar las normatividades disponibles
        normatividad_custom: '', // Se procesará después
        estado: proc.estado || 'borrador'
      })
      
      // Guardar los textos originales para procesarlos después
      window.definicionesOriginal = proc.definiciones || ''
      window.normatividadOriginal = normatividadTexto
      
      // Cargar actividades
      setActividades(proc.actividades || [])
      
      // Cargar diagrama si existe
      if (proc.diagrama) {
        setDiagramCode(proc.diagrama.mermaid_code || '')
        setDiagramExists(true)
      } else {
        setDiagramCode('')
        setDiagramExists(false)
      }
    } catch (error) {
      setError('Error al cargar procedimiento: ' + (error.response?.data?.detail || error.message))
    }
  }

  // Funciones para recargar datos específicos cuando el usuario interactúa
  const handleRefreshData = async () => {
    console.log('🔄 Recargando datos por interacción del usuario (edición)...')
    await loadReferenceData()
  }

  const loadReferenceData = async () => {
    try {
      const token = localStorage.getItem('token')
      
      // Cargar datos de referencia en paralelo (con límite máximo permitido)
      const [gestionesResp, rolesResp, definicionesResp, normatividadResp, procesosResp] = await Promise.all([
        axios.get('/api/gestiones?limit=100', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/roles-responsabilidad?limit=100', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/definiciones?limit=100', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/normatividad?limit=100', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/procesos?activo=true&limit=100', { headers: { Authorization: `Bearer ${token}` } })
      ])

      setGestiones(gestionesResp.data || [])
      setRoles(rolesResp.data || [])
      
      const definicionesData = definicionesResp.data || []
      setDefiniciones(definicionesData)
      
      const procesosData = procesosResp.data || []
      setProcesos(procesosData)
      
      // Buscar el proceso actual si existe
      if (formData.proceso && typeof formData.proceso === 'string') {
        const procesoEncontrado = procesosData.find(p => p.nombre === formData.proceso)
        if (procesoEncontrado) {
          setFormData(prev => ({ ...prev, proceso: procesoEncontrado }))
        }
      }
      
      // Procesar definiciones existentes para detectar predefinidas
      if (window.definicionesOriginal) {
        procesarDefinicionesExistentes(definicionesData, window.definicionesOriginal)
        delete window.definicionesOriginal
      }
      
      // Agregar opción "otra" a normatividades
      const normatividadesConOtra = [
        ...(normatividadResp.data || []),
        { id: 'otra', titulo: 'Otra (especificar manualmente)', categoria: 'otra' }
      ]
      setNormatividades(normatividadesConOtra)
      
      // Procesar normatividad existente para detectar predefinidas
      if (window.normatividadOriginal) {
        procesarNormatividadExistente(normatividadResp.data || [], window.normatividadOriginal)
        delete window.normatividadOriginal
      }
      
    } catch (error) {
      console.error('Error al cargar datos de referencia:', error)
    } finally {
      setLoading(false)
    }
  }

  const procesarDefinicionesExistentes = (definicionesDisponibles, definicionesTexto) => {
    if (!definicionesTexto || !definicionesDisponibles.length) return

    // Intentar encontrar coincidencias exactas por término
    const seleccionadas = []

    definicionesDisponibles.forEach(def => {
      // Buscar el término en el texto
      const regex = new RegExp(`\\b${def.termino}\\b`, 'i')
      if (regex.test(definicionesTexto)) {
        seleccionadas.push(def)
      }
    })

    if (seleccionadas.length > 0) {
      setFormData(prev => ({
        ...prev,
        definiciones: seleccionadas
      }))
    }
  }

  const procesarNormatividadExistente = (normatividadesDisponibles, normatividadTexto) => {
    if (!normatividadTexto || !normatividadesDisponibles.length) {
      return
    }

    console.log('🔍 Procesando normatividad existente:', {
      texto: normatividadTexto,
      disponibles: normatividadesDisponibles.length
    })

    // Dividir el texto por comas o punto y coma para obtener elementos individuales
    const textoParts = normatividadTexto.split(/[,;]+/).map(part => part.trim()).filter(part => part.length > 0)
    
    const seleccionadas = []
    let textoRestante = normatividadTexto

    // Buscar coincidencias exactas por título
    normatividadesDisponibles.forEach(norm => {
      if (norm.id === 'otra') return // Saltear la opción "otra"
      
      textoParts.forEach(parte => {
        // Buscar coincidencia exacta o parcial
        if (norm.titulo && (
          parte.toLowerCase().includes(norm.titulo.toLowerCase()) || 
          norm.titulo.toLowerCase().includes(parte.toLowerCase())
        )) {
          if (!seleccionadas.find(s => s.id === norm.id)) {
            seleccionadas.push(norm)
            // Remover del texto restante
            textoRestante = textoRestante.replace(new RegExp(parte.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '').trim()
          }
        }
      })
    })

    // Limpiar texto restante de comas y puntos y coma extra
    textoRestante = textoRestante.replace(/^[,;\s]+|[,;\s]+$/g, '').replace(/[,;]+/g, ', ')

    console.log('✅ Normatividad procesada:', {
      seleccionadas: seleccionadas.map(s => s.titulo),
      textoRestante
    })

    // Actualizar el estado con las normatividades seleccionadas y el texto restante
    setFormData(prev => ({
      ...prev,
      normatividades: seleccionadas,
      normatividad_custom: textoRestante
    }))

    // Si hay texto custom, mostrar el campo
    if (textoRestante) {
      setShowCustomNormatividad(true)
    }
  }

  const handleNormatividadChange = (event, newValue) => {
    const hasOtra = newValue.some(norm => norm.id === 'otra')
    setShowCustomNormatividad(hasOtra)
    setFormData(prev => ({ ...prev, normatividades: newValue }))
  }

  const handleCodigoChange = (e) => {
    const input = e.target.value
    setCodigoInput(input)
    
    if (selectedGestion && input) {
      setFormData(prev => ({ ...prev, codigo: `${selectedGestion.codigo}-${input}` }))
    } else {
      setFormData(prev => ({ ...prev, codigo: '' }))
    }
  }

  const handleGestionChange = (e) => {
    const gestionId = e.target.value
    setFormData(prev => ({ ...prev, gestion_id: gestionId }))
  }

  // Funciones para actividades
  const handleAddActivity = () => {
    setActivityForm({
      titulo: '',
      descripcion: '',
      informacion_soporte: '',
      informacion_generada: '',
      responsables: []
    })
    setEditingActivity(null)
    setOpenActivityDialog(true)
  }

  const handleEditActivity = (index) => {
    const activity = actividades[index]
    setActivityForm({
      titulo: activity.titulo || '',
      descripcion: activity.descripcion || '',
      informacion_soporte: activity.informacion_soporte || '',
      informacion_generada: activity.informacion_generada || '',
      responsables: activity.responsables || []
    })
    setEditingActivity(index)
    setOpenActivityDialog(true)
  }

  const handleDeleteActivity = (index) => {
    if (window.confirm('¿Está seguro de eliminar esta actividad?')) {
      const newActividades = actividades.filter((_, i) => i !== index)
      setActividades(newActividades)
    }
  }

  const handleActivitySubmit = async () => {
    if (editingActivity !== null) {
      // Editar actividad existente
      const newActividades = [...actividades]
      newActividades[editingActivity] = { ...activityForm }
      setActividades(newActividades)
    } else {
      // Agregar nueva actividad
      setActividades([...actividades, { ...activityForm }])
    }
    setOpenActivityDialog(false)
  }

  const handleDiagramSave = async (code, svgContent) => {
    try {
      const token = localStorage.getItem('token')
      await axios.post(`/api/procedimientos/${id}/diagrama`, {
        mermaid_code: code,
        svg_rendered: svgContent,
        auto_generated: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setDiagramCode(code)
      setDiagramSvg(svgContent)
      setDiagramExists(true)
      setDiagramHasUnsavedChanges(false)
      setSuccess('Diagrama guardado exitosamente')
      setTimeout(() => setSuccess(null), 3000)
    } catch (error) {
      setError('Error al guardar diagrama: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Verificar si hay diagrama renderizado sin guardar
    if (diagramHasUnsavedChanges) {
      setShowUnsavedDiagramDialog(true)
      return
    }

    await submitProcedimiento()
  }

  const submitProcedimiento = async () => {
    setSaving(true)
    setError(null)

    try{
      const token = localStorage.getItem('token')
      
      // 1. Actualizar información básica del procedimiento
      const updateData = {
        codigo: formData.codigo,
        titulo: formData.titulo,
        proceso: typeof formData.proceso === 'object' ? formData.proceso?.nombre : formData.proceso,
        gestion_id: formData.gestion_id,
        objetivo: formData.objetivo,
        alcance: formData.alcance,
        version: formData.version,
        estado: formData.estado,
        definiciones: Array.isArray(formData.definiciones)
          ? formData.definiciones.map(def => `${def.termino}: ${def.definicion}`).join('; ')
          : '',
        responsables: formData.responsables,
        // Procesar normatividades
        normatividad: Array.isArray(formData.normatividades) 
          ? formData.normatividades
              .filter(norm => norm.id !== 'otra')
              .map(norm => norm.titulo)
              .concat(formData.normatividad_custom ? [formData.normatividad_custom] : [])
              .join(', ')
          : formData.normatividad_custom || ''
      }

      await axios.put(`/api/procedimientos/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      })

      // 2. Obtener actividades existentes del procedimiento
      const currentProcResponse = await axios.get(`/api/procedimientos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const existingActividades = currentProcResponse.data.actividades || []

      // 3. Actualizar actividades
      // Eliminar actividades que ya no están en la lista local
      for (const existingActividad of existingActividades) {
        const stillExists = actividades.some(a => a.id === existingActividad.id)
        if (!stillExists) {
          await axios.delete(`/api/procedimientos/${id}/actividades/${existingActividad.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        }
      }

      // Crear o actualizar actividades
      for (const actividad of actividades) {
        const actividadData = {
          titulo: actividad.titulo,
          descripcion: actividad.descripcion,
          informacion_soporte: actividad.informacion_soporte || '',
          informacion_generada: actividad.informacion_generada || '',
          responsables: actividad.responsables || []
        }

        if (actividad.id && existingActividades.some(a => a.id === actividad.id)) {
          // Actualizar actividad existente
          await axios.put(`/api/procedimientos/${id}/actividades/${actividad.id}`, actividadData, {
            headers: { Authorization: `Bearer ${token}` }
          })
        } else {
          // Crear nueva actividad
          await axios.post(`/api/procedimientos/${id}/actividades`, actividadData, {
            headers: { Authorization: `Bearer ${token}` }
          })
        }
      }

      setSuccess('Procedimiento actualizado exitosamente')
      setTimeout(() => navigate('/procedimientos'), 2000)
      
    } catch (error) {
      setError('Error al actualizar procedimiento: ' + (error.response?.data?.detail || error.message))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando procedimiento...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Description sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Editar Procedimiento
          </Typography>
          {formData.codigo && (
            <Chip label={formData.codigo} color="primary" variant="outlined" />
          )}
          {formData.estado && (
            <Chip 
              label={`ESTADO: ${formData.estado.toUpperCase()}`} 
              color={getEstadoColor(formData.estado)} 
              variant="filled"
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={() => navigate('/procedimientos')}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Información General */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Información General"
                avatar={<Business />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Gestión</InputLabel>
                      <Select
                        value={formData.gestion_id}
                        label="Gestión"
                        onChange={handleGestionChange}
                      >
                        {gestiones.map((gestion) => (
                          <MenuItem key={gestion.id} value={gestion.id}>
                            {gestion.codigo} - {gestion.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Código del Procedimiento"
                      value={codigoInput}
                      onChange={handleCodigoChange}
                      placeholder={selectedGestion ? "0101" : "Seleccione gestión primero"}
                      disabled={!selectedGestion}
                      required
                      InputProps={{
                        startAdornment: selectedGestion ? (
                          <Typography variant="body2" sx={{ mr: 1, color: 'primary.main', fontWeight: 'bold' }}>
                            {selectedGestion.codigo}-
                          </Typography>
                        ) : null,
                      }}
                      helperText={selectedGestion ? `Código completo: ${formData.codigo || selectedGestion.codigo + '-'}` : ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Versión"
                      value={formData.version}
                      onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Título del Procedimiento"
                      value={formData.titulo}
                      onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={procesos}
                      getOptionLabel={(option) => option?.nombre || ''}
                      value={formData.proceso}
                      onChange={(event, newValue) => {
                        setFormData({ ...formData, proceso: newValue })
                      }}
                      onFocus={handleRefreshData}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Proceso"
                          placeholder="Seleccionar proceso al que pertenece..."
                          required
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body1">
                              {option.nombre}
                            </Typography>
                            {option.descripcion && (
                              <Typography variant="body2" color="text.secondary">
                                {option.descripcion}
                              </Typography>
                            )}
                            {option.gestion_nombre && (
                              <Typography variant="caption" color="primary">
                                Gestión: {option.gestion_codigo} - {option.gestion_nombre}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                      noOptionsText="No hay procesos disponibles"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Estado</InputLabel>
                      <Select
                        value={formData.estado}
                        label="Estado"
                        onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                      >
                        <MenuItem value="borrador">BORRADOR</MenuItem>
                        <MenuItem value="revision">REVISION</MenuItem>
                        <MenuItem value="aprobado">APROBADO</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Objetivo"
                      multiline
                      rows={3}
                      value={formData.objetivo}
                      onChange={(e) => setFormData({ ...formData, objetivo: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Alcance"
                      multiline
                      rows={3}
                      value={formData.alcance}
                      onChange={(e) => setFormData({ ...formData, alcance: e.target.value })}
                      required
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Responsables */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Responsables"
                avatar={<People />}
              />
              <CardContent>
                <Autocomplete
                  multiple
                  options={roles}
                  getOptionLabel={(option) => option.nombre}
                  value={formData.responsables}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, responsables: newValue })
                  }}
                  onFocus={handleRefreshData}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar Responsables"
                      placeholder="Buscar roles de responsabilidad..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.nombre}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Normatividad */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Normatividad"
                avatar={<Gavel />}
              />
              <CardContent>
                <Autocomplete
                  multiple
                  options={normatividades}
                  getOptionLabel={(option) => option.titulo}
                  value={formData.normatividades}
                  onChange={handleNormatividadChange}
                  onFocus={handleRefreshData}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Normatividad"
                      placeholder="Seleccionar marco normativo aplicable..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.titulo}
                        {...getTagProps({ index })}
                        key={option.id}
                        color={option.id === 'otra' ? 'secondary' : 'default'}
                      />
                    ))
                  }
                />
                {showCustomNormatividad && (
                  <TextField
                    fullWidth
                    sx={{ mt: 2 }}
                    label="Especificar Normatividad"
                    multiline
                    rows={3}
                    value={formData.normatividad_custom}
                    onChange={(e) => setFormData({ ...formData, normatividad_custom: e.target.value })}
                    placeholder="Ingrese la normatividad específica..."
                    helperText="Escriba la normatividad que no aparece en la lista predefinida"
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Definiciones */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Definiciones"
                avatar={<MenuBook />}
              />
              <CardContent>
                <Autocomplete
                  multiple
                  options={definiciones}
                  getOptionLabel={(option) => option.termino}
                  value={formData.definiciones}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, definiciones: newValue })
                  }}
                  onFocus={handleRefreshData}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Definiciones"
                      placeholder="Seleccionar definiciones aplicables..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.termino}
                        {...getTagProps({ index })}
                        key={option.id}
                        color="primary"
                      />
                    ))
                  }
                />
                {formData.definiciones.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Definiciones seleccionadas:
                    </Typography>
                    {formData.definiciones.map((def) => (
                      <Box key={def.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {def.termino}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {def.definicion}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Actividades */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Actividades del Procedimiento"
                avatar={<MenuBook />}
                action={
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={handleAddActivity}
                  >
                    Agregar Actividad
                  </Button>
                }
              />
              <CardContent>
                {actividades.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Título</TableCell>
                          <TableCell>Descripción</TableCell>
                          <TableCell>Responsables</TableCell>
                          <TableCell align="center">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {actividades.map((actividad, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {actividad.titulo}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {actividad.descripcion.substring(0, 100)}
                                {actividad.descripcion.length > 100 ? '...' : ''}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(actividad.responsables || []).map((resp, idx) => (
                                  <Chip 
                                    key={idx}
                                    label={resp.nombre || `ID: ${resp.id}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditActivity(index)}
                                title="Editar"
                              >
                                <Edit />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteActivity(index)}
                                title="Eliminar"
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                    No hay actividades definidas. Haga clic en "Agregar Actividad" para comenzar.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Diagrama de Flujo */}
          <Grid item xs={12}>
            <Card ref={diagramRef}>
              <CardHeader
                title="Diagrama de Flujo (Flujograma)"
                avatar={<AccountTree />}
              />
              <CardContent>
                <DiagramEditor
                  initialCode={diagramCode}
                  onSave={handleDiagramSave}
                  onChange={(code, svg, hasUnsavedChanges) => {
                    setDiagramCode(code)
                    setDiagramSvg(svg)
                    setDiagramHasUnsavedChanges(hasUnsavedChanges)
                  }}
                  actividades={actividades}
                  procedimientoId={parseInt(id)}
                  readOnly={false}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>

      {/* Dialog para actividades */}
      <Dialog open={openActivityDialog} onClose={() => setOpenActivityDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingActivity !== null ? 'Editar Actividad' : 'Nueva Actividad'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Título de la Actividad"
                  value={activityForm.titulo}
                  onChange={(e) => setActivityForm({ ...activityForm, titulo: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción"
                  multiline
                  rows={3}
                  value={activityForm.descripcion}
                  onChange={(e) => setActivityForm({ ...activityForm, descripcion: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Información de Soporte"
                  multiline
                  rows={2}
                  value={activityForm.informacion_soporte}
                  onChange={(e) => setActivityForm({ ...activityForm, informacion_soporte: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Información Generada"
                  multiline
                  rows={2}
                  value={activityForm.informacion_generada}
                  onChange={(e) => setActivityForm({ ...activityForm, informacion_generada: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={roles}
                  getOptionLabel={(option) => option.nombre}
                  value={activityForm.responsables}
                  onChange={(event, newValue) => {
                    setActivityForm({ ...activityForm, responsables: newValue })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Responsables de la Actividad"
                      placeholder="Seleccionar responsables..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.nombre}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenActivityDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleActivitySubmit} 
            variant="contained"
            disabled={!activityForm.titulo || !activityForm.descripcion}
          >
            {editingActivity !== null ? 'Actualizar' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Diagrama No Guardado */}
      <Dialog
        open={showUnsavedDiagramDialog}
        onClose={() => setShowUnsavedDiagramDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          ⚠️ Diagrama sin guardar
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Ha generado o editado un diagrama pero no lo ha guardado.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Si continúa sin guardar el diagrama, solo se guardará el código Mermaid pero no la visualización renderizada (SVG),
            lo que significa que el diagrama no se mostrará en el PDF ni en la vista previa del procedimiento.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
            ¿Desea guardar el diagrama antes de continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowUnsavedDiagramDialog(false)
              submitProcedimiento()
            }}
            color="error"
          >
            Continuar sin guardar diagrama
          </Button>
          <Button
            onClick={() => {
              setShowUnsavedDiagramDialog(false)
              // Hacer scroll al diagrama con un pequeño delay para que el dialog se cierre
              setTimeout(() => {
                diagramRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                })
              }, 100)
            }}
            variant="contained"
            autoFocus
          >
            Volver a guardar diagrama
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProcedimientoEdit